import type { SledTeam } from '@sdd-maturity/shared-types';
import { isRootNode } from './hierarchy-route';
import { isPhantom, resolveDescendantTeams } from './hierarchy';

/** Tier-number → label, mirroring DEFAULT_TIER_CONFIG in @sdd-maturity/shared-types.
 *  Kept local because the dashboard consumes shared-types as types only (its CJS
 *  build doesn't bundle cleanly as a runtime import). */
const TIER_LABELS: Record<number, string> = {
  1: 'Portfolio',
  2: 'Delivery Stream',
  3: 'Release Train',
  4: 'Team',
};

export interface ScopeInfo {
  /** The deepest selected node, or null when nothing is selected. */
  node: SledTeam | null;
  /** True when a single tier-4 team is selected (Overview → scorecard). */
  isSingleTeam: boolean;
  /** Children one level below `node` (empty for a single team or no selection). */
  children: SledTeam[];
}

/**
 * Resolve the Overview scope from the URL-resolved node. The root "Cox Automotive"
 * scope rolls up all tier-1 portfolios; multi-team scopes (Portfolio/Stream/Train)
 * drive the Standings table; a single team drives the two-phase scorecard. Children
 * are the nodes exactly one tier below `node` (portfolios for the root).
 */
export function resolveScope(node: SledTeam | null, allTeams: SledTeam[]): ScopeInfo {
  const childrenOf = (parentNumber: string, tier: number): SledTeam[] =>
    allTeams
      .filter(
        (t) =>
          t.tier === tier &&
          t.parentNumber === parentNumber &&
          !isPhantom(t, allTeams) &&
          // Tier-4 children (teams) have no descendants to count — the phantom
          // check above is their only filter. Tier<4 children (Portfolio/Stream/
          // Train) are hidden when they roll up zero descendant teams, mirroring
          // the leaderboard's resolveChildGroups (lib/hierarchy.ts) so the
          // Standings table and rollup leaderboard agree on which nodes exist.
          (t.tier === 4 || resolveDescendantTeams(allTeams, t.number).length > 0),
      )
      .sort((a, b) => a.name.localeCompare(b.name));

  if (!node) return { node: null, isSingleTeam: false, children: [] };
  if (isRootNode(node)) {
    // The root has no real number for a parentNumber walk; its children are all
    // tier-1 portfolios (never phantom — phantom is a tier-4-only concept), but
    // still subject to the same "has ≥1 descendant team" rule as every other
    // tier<4 node — a portfolio with a SLED-mapping gap is hidden here exactly
    // like an empty Delivery Stream/Release Train, with no tier exception.
    const children = allTeams
      .filter((t) => t.tier === 1 && resolveDescendantTeams(allTeams, t.number).length > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
    return { node, isSingleTeam: false, children };
  }
  if (node.tier === 4) {
    return { node, isSingleTeam: true, children: [] };
  }
  // A non-leaf whose own subtree has zero REAL tier-4 descendants collapses
  // straight to its scorecard too — but only when there's an actual synthetic
  // direct-repo leaf standing in for it. A node can have zero real descendants
  // for two different reasons: (a) it has an injected synthetic leaf (its own
  // repos, no teams) — that's the single-team case this branch exists for; or
  // (b) it's genuinely empty (no teams, no direct-repo mapping either) — pre-
  // slice behavior for that case is no synthetic row and no isSingleTeam
  // change, so it must fall through to the normal multi-team path (which then
  // renders zero children, same as before this feature existed). Checking
  // both conditions — rather than realDescendantCount === 0 alone — is what
  // keeps case (b) from being mistaken for case (a).
  const descendants = resolveDescendantTeams(allTeams, node.number);
  const realDescendantCount = descendants.filter((leaf) => !leaf.number.startsWith('SYNTH#')).length;
  const hasSyntheticLeaf = descendants.some((leaf) => leaf.number.startsWith('SYNTH#'));
  if (realDescendantCount === 0 && hasSyntheticLeaf) {
    return { node, isSingleTeam: true, children: [] };
  }
  return { node, isSingleTeam: false, children: childrenOf(node.number, node.tier + 1) };
}

/**
 * Singular + plural label for the nodes one tier below `node` — e.g. a Portfolio's
 * children are "Delivery Stream"/"Delivery Streams". Children are always exactly one
 * tier down (see resolveScope), so the child tier is `node.tier + 1`. Falls back to
 * "Team" when the node/tier is unknown. Simple `+ "s"` pluralization is correct for
 * all four tier labels.
 */
export function childLabel(node: SledTeam | null): { singular: string; plural: string } {
  const childTier = (node?.tier ?? 3) + 1;
  const singular = TIER_LABELS[childTier] ?? 'Team';
  return { singular, plural: `${singular}s` };
}

/** The node's OWN tier noun (e.g. a Portfolio → "Portfolio"). Prefers the node's
 *  configured tierLabel, then the default map; falls back to "scope" when unknown. */
export function tierNoun(node: SledTeam | null): string {
  if (!node) return 'scope';
  return node.tierLabel || TIER_LABELS[node.tier] || 'scope';
}
